// contexts/SocketContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import io from "socket.io-client";

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

export const SocketProvider = ({ children, user }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (user) {
      // Normalize user.id
      const userId = user.id || user.userID;

      const newSocket = io(process.env.REACT_APP_SOCKET_URL, {
        query: { userId },
        transports: ["websocket", "polling"],
      });

      newSocket.on("connect", () => {
        console.log("Connected to server");
        setIsConnected(true);
      });

      newSocket.on("disconnect", () => {
        console.log("Disconnected from server");
        setIsConnected(false);
      });

      newSocket.on("connect_error", (error) => {
        console.error("Connection error:", error);
        setIsConnected(false);
      });

      setSocket(newSocket);

      return () => {
        newSocket.close();
      };
    }
  }, [user]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};

// 🔑 Normalize every message to avoid UI crashes
const normalizeMessage = (msg, currentUser) => {
  // console.log("msg ======>", msg);
  const sender =
    typeof msg.sender === "object" && msg.sender !== null
      ? {
          id: msg.sender.id || msg.sender._id || msg.sender.userID || "unknown",
          fullName: msg.sender.fullName || msg.sender.name || "Unknown User",
          email: msg.sender.email || "",
        }
      : {
          id: msg.sender || "unknown",
          fullName: "Unknown User",
        };

  const currentUserId = currentUser?.id || currentUser?.userID;

  return {
    id: msg.id || msg._id || Date.now().toString(),
    message: msg.message || "",
    timestamp: msg.timestamp || new Date().toISOString(),
    sender,
    isOwn: sender.id === currentUserId,
    status: msg.status || "sent",
    readBy: msg.readBy || [],
  };
};

export const useGroupChat = (groupId, currentUser) => {
  const { socket, isConnected } = useSocket();
  const [messages, setMessages] = useState([]);
  const [onlineMembers, setOnlineMembers] = useState([]);
  const [typing, setTyping] = useState([]);
  const [isJoined, setIsJoined] = useState(false);

  const typingTimeoutRef = useRef(null);

  // Join group
  useEffect(() => {
    if (socket && isConnected && groupId && currentUser && !isJoined) {
      socket.emit("join-group", {
        groupId,
        user: currentUser,
      });
      setIsJoined(true);
    }
  }, [socket, isConnected, groupId, currentUser, isJoined]);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    // Message events
    const handleMessageHistory = (messageHistory) => {
      // console.log("MessageHistory ==========>", messageHistory);
      const processedMessages = messageHistory.map((msg) =>
        normalizeMessage(msg, currentUser)
      );
      setMessages(processedMessages);
    };

    const handleNewMessage = (messageData) => {
      const processedMessage = normalizeMessage(messageData, currentUser);
      setMessages((prev) => [...prev, processedMessage]);
    };

    const handleMessageDelivered = ({ messageId, status }) => {
      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? { ...msg, status } : msg))
      );
    };

    const handleMessageRead = ({ messageId, readBy }) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? {
                ...msg,
                status: "read",
                readBy: [...(msg.readBy || []), readBy],
              }
            : msg
        )
      );
    };

    // Online users events
    const handleUsersOnline = (users) => {
      setOnlineMembers(users);
    };

    const handleUserJoined = (user) => {
      setOnlineMembers((prev) => [
        ...prev.filter((u) => u.id !== user.id),
        user,
      ]);
    };

    const handleUserLeft = (userId) => {
      setOnlineMembers((prev) => prev.filter((user) => user.id !== userId));
    };

    // Typing events
    const handleUserTyping = ({ userId, userName }) => {
      const currentUserId = currentUser?.id || currentUser?.userID;
      if (userId !== currentUserId) {
        setTyping((prev) => {
          if (!prev.find((u) => u.id === userId)) {
            return [...prev, { id: userId, name: userName }];
          }
          return prev;
        });
      }
    };

    const handleUserStoppedTyping = ({ userId }) => {
      setTyping((prev) => prev.filter((u) => u.id !== userId));
    };

    // Register event listeners
    socket.on("message-history", handleMessageHistory);
    socket.on("new-message", handleNewMessage);
    socket.on("message-delivered", handleMessageDelivered);
    socket.on("message-read", handleMessageRead);
    socket.on("users-online", handleUsersOnline);
    socket.on("user-joined", handleUserJoined);
    socket.on("user-left", handleUserLeft);
    socket.on("user-typing", handleUserTyping);
    socket.on("user-stopped-typing", handleUserStoppedTyping);

    return () => {
      socket.off("message-history", handleMessageHistory);
      socket.off("new-message", handleNewMessage);
      socket.off("message-delivered", handleMessageDelivered);
      socket.off("message-read", handleMessageRead);
      socket.off("users-online", handleUsersOnline);
      socket.off("user-joined", handleUserJoined);
      socket.off("user-left", handleUserLeft);
      socket.off("user-typing", handleUserTyping);
      socket.off("user-stopped-typing", handleUserStoppedTyping);
    };
  }, [socket, currentUser]);

  // Send message function
  const sendMessage = useCallback(
    (messageText) => {
      if (!socket || !isConnected || !messageText.trim()) return;

      // console.log("send messages consoled------>", groupId, currentUser);

      const messageData = normalizeMessage(
        {
          id: Date.now().toString(),
          groupId,
          sender: currentUser,
          message: messageText.trim(),
          timestamp: new Date().toISOString(),
          isOwn: true,
          status: "sending",
        },
        currentUser
      );

      // Optimistically add message to UI
      // setMessages((prev) => [...prev, messageData]);

      // Send message through socket
      socket.emit("send-message", messageData);

      // Stop typing indicator
      const currentUserId = currentUser?.id || currentUser?.userID;
      socket.emit("stop-typing", { groupId, userId: currentUserId });
    },
    [socket, isConnected, groupId, currentUser]
  );

  // Typing functions
  const startTyping = useCallback(() => {
    if (!socket || !isConnected) return;

    const currentUserId = currentUser?.id || currentUser?.userID;

    socket.emit("typing", {
      groupId,
      userId: currentUserId,
      userName: currentUser?.fullName,
    });

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      socket.emit("stop-typing", {
        groupId,
        userId: currentUserId,
      });
    }, 1000);
  }, [socket, isConnected, groupId, currentUser]);

  const stopTyping = useCallback(() => {
    if (!socket || !isConnected) return;

    const currentUserId = currentUser?.id || currentUser?.userID;

    socket.emit("stop-typing", {
      groupId,
      userId: currentUserId,
    });

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  }, [socket, isConnected, groupId, currentUser]);

  // Mark message as read
  const markMessageAsRead = useCallback(
    (messageId) => {
      if (!socket || !isConnected) return;

      const currentUserId = currentUser?.id || currentUser?.userID;

      socket.emit("mark-as-read", {
        messageId,
        groupId,
        userId: currentUserId,
        userName: currentUser?.fullName,
      });
    },
    [socket, isConnected, groupId, currentUser]
  );

  return {
    messages,
    onlineMembers,
    typing,
    isConnected,
    sendMessage,
    startTyping,
    stopTyping,
    markMessageAsRead,
  };
};

export const useAuth = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getUserFromStorage = () => {
      try {
        const user = localStorage.getItem("workgs-user");
        if (user) {
          const parsed = JSON.parse(user);
          // Normalize id
          if (parsed.userID && !parsed.id) {
            parsed.id = parsed.userID;
          }
          return parsed;
        }
      } catch (error) {
        console.error("Error parsing user from storage:", error);
      }
      return null;
    };

    const user = getUserFromStorage();
    setCurrentUser(user);
    setIsLoading(false);
  }, []);

  return { currentUser, isLoading };
};
